// AnalisisCalidadDetalle.js
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Edit3, Send, Printer, PlusCircle, Trash2, Save } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input, InputNumber, Textarea } from "./components/ui/input";
import { Select } from "./components/ui/select";
import { Card, CardHeader, CardTitle, CardExtra, CardContent } from "./components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from "./components/ui/dialog";
import { toast } from "./lib/toast";
import apiUrl from "./config/config";

const token = "Bearer 0j2whpl3*s+2/j-hCl!!-5A-8Corubp5[gqzhGrA.b*hBdO[+r.yGIFpvpOqv";

const parseResponse = (data) => {
  try {
    if (data && typeof data === "object") {
      if ("d" in data) return typeof data.d === "string" ? JSON.parse(data.d) : data.d;
      return data;
    }
    return JSON.parse(String(data ?? ""));
  } catch { return null; }
};

const scoreStyles = (p) => {
  const v = String(p ?? "").toUpperCase();
  if (v === "A") return { bg: "#52c41a", fg: "#fff" };
  if (v === "B") return { bg: "#faad14", fg: "#000" };
  if (v === "C") return { bg: "#ff4d4f", fg: "#fff" };
  if (v) return { bg: "#000", fg: "#fff" };
  return { bg: "#2f54eb", fg: "#fff" };
};

const norm = (s) => String(s || "").normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}%`);
const sumTotals = (arr) =>
  arr.reduce(
    (acc, it) => ({ valor: acc.valor + (Number(it.valor) || 0), porcentaje: acc.porcentaje + (Number(it.porcentaje) || 0) }),
    { valor: 0, porcentaje: 0 }
  );

const tipoCanon = (s) => {
  const t = norm(s || "");
  if (t.includes("condicion")) return "condicion";
  if (t.includes("calidad")) return "calidad";
  return t || "calidad";
};

export default function AnalisisCalidadDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const backToList = () => {
    const qs = location.search || "";
    navigate(`/dashboard/calidad/analisis${qs}`);
  };

  const [loading, setLoading] = useState(false);
  const [productores, setProductores] = useState([]);
  const [variedades, setVariedades] = useState([]);
  const [catalogoCalibres, setCatalogoCalibres] = useState([]);
  const [catalogoDefectos, setCatalogoDefectos] = useState([]);

  const [enc, setEnc] = useState(null);
  const [calibres, setCalibres] = useState([]);
  const [defectos, setDefectos] = useState([]);
  const [firmeza, setFirmeza] = useState([]);
  const [puntajeFinal, setPuntajeFinal] = useState(null);
  const [imagenes, setImagenes] = useState([]);
  const [imgPreviewIndex, setImgPreviewIndex] = useState(0);
  const [imgPreviewVisible, setImgPreviewVisible] = useState(false);

  // Modal calibres
  const [calModalOpen, setCalModalOpen] = useState(false);
  const [savingCal, setSavingCal] = useState(false);
  const [calibresModal, setCalibresModal] = useState([]);

  // Modal defectos
  const [defModalOpen, setDefModalOpen] = useState(false);
  const [savingDef, setSavingDef] = useState(false);
  const [defectosModal, setDefectosModal] = useState([]);

  // Modal firmeza
  const [firModalOpen, setFirModalOpen] = useState(false);
  const [savingFirmeza, setSavingFirmeza] = useState(false);
  const [firmezaModal, setFirmezaModal] = useState([]);

  const defectosCalidad   = useMemo(() => defectos.filter((d) => tipoCanon(d.tipo) === "calidad"),   [defectos]);
  const defectosCondicion = useMemo(() => defectos.filter((d) => tipoCanon(d.tipo) === "condicion"), [defectos]);
  const totCalidad   = useMemo(() => sumTotals(defectosCalidad),   [defectosCalidad]);
  const totCondicion = useMemo(() => sumTotals(defectosCondicion), [defectosCondicion]);

  const promedioFirmeza = useMemo(() => {
    if (!Array.isArray(firmeza) || firmeza.length === 0) return null;
    const nums = firmeza.map((x) => Number(x.valor)).filter((n) => !Number.isNaN(n));
    if (nums.length === 0) return null;
    return nums.reduce((a, b) => a + b, 0) / nums.length;
  }, [firmeza]);

  const idToTipoMap = useMemo(() => {
    const m = new Map();
    catalogoDefectos.forEach((d) => m.set(d.id, tipoCanon(d.tipo)));
    return m;
  }, [catalogoDefectos]);

  const getTipoById = (id) => idToTipoMap.get(id) || null;

  const nombreDefecto = (d) => {
    if (d?.calidad) return d.calidad;
    const xid = d?.idcalidad ?? d?.id ?? null;
    if (xid == null) return "—";
    const found = catalogoDefectos.find((x) => String(x.id) === String(xid));
    return found?.nombre ?? String(xid);
  };

  // ===== fetchers =====
  const fetchProductores = async () => {
    try {
      const response = await axios.get(`${apiUrl}/ServiceFamilyGrowers.asmx/GetFamilyProductores`, { headers: { Authorization: token } });
      const responseData = response.data.d ? JSON.parse(response.data.d) : response.data;
      setProductores(Array.isArray(responseData) ? responseData : (responseData?.data || []));
    } catch (e) { console.error(e); toast.error("No se pudieron cargar los productores."); }
  };

  const fetchVariedades = async () => {
    try {
      const response = await axios.get(`${apiUrl}/ServiceFamilyGrowers.asmx/GetFamilyVariedad`, { headers: { Authorization: token } });
      const responseData = response.data.d ? JSON.parse(response.data.d) : response.data;
      setVariedades(Array.isArray(responseData) ? responseData : (responseData?.data || []));
    } catch (e) { console.error(e); toast.error("No se pudieron cargar las variedades."); }
  };

  const fetchCatalogoCalibres = async () => {
    try {
      const response = await axios.get(`${apiUrl}/ServiceFamilyGrowers.asmx/GetCalibres`, { headers: { Authorization: token } });
      const responseData = response.data?.d ? JSON.parse(response.data.d) : response.data;
      setCatalogoCalibres(Array.isArray(responseData) ? responseData : (responseData?.data || []));
    } catch (e) { console.error(e); toast.error("No se pudo cargar el catálogo de calibres."); }
  };

  const fetchCatalogoDefectos = async () => {
    try {
      const response = await axios.get(`${apiUrl}/ServiceFamilyGrowers.asmx/GetDefectosCalidadCondicion`, { headers: { Authorization: token } });
      const raw = response.data?.d ? JSON.parse(response.data.d) : response.data;
      setCatalogoDefectos(raw);
    } catch (e) { console.error(e); toast.error("No se pudo cargar el catálogo de defectos."); }
  };

  const fetchImagenes = async () => {
    try {
      const res = await axios.get(`${apiUrl}/ServiceFamilyGrowers.asmx/GetQualityImageFiles`, { headers: { Authorization: token }, params: { id } });
      const data = parseResponse(res.data) ?? (res.data?.d ? JSON.parse(res.data.d) : res.data);
      const list = Array.isArray(data?.Imagenes) ? data.Imagenes : [];
      const normalized = list.map((x) => {
        const nombre = String(x?.nombrearchivo ?? "").trim();
        const url = x?.url ? String(x.url) : (nombre ? `https://ws.packingapp.cl/ArchivosCalidad/${nombre}` : "");
        return { nombrearchivo: nombre, url };
      }).filter((it) => it.url);
      setImagenes(normalized);
    } catch (e) { console.error(e); toast.error("No se pudieron cargar las imágenes."); }
  };

  const fetchDetalle = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/ServiceFamilyGrowers.asmx/GetQualityAnalysisData`, { headers: { Authorization: token }, params: { id } });
      const data = parseResponse(res.data) ?? (res.data?.d ? JSON.parse(res.data.d) : res.data);
      const encRow = (data?.Encabezado && data.Encabezado[0]) || null;
      console.log(data);
      setEnc(encRow ? { ...encRow, fecha: encRow.fecha ? dayjs(encRow.fecha) : null } : null);
      setCalibres(Array.isArray(data?.Calibres) ? data.Calibres : []);
      setDefectos(
        Array.isArray(data?.Defectos)
          ? data.Defectos.map((d) => ({ ...d, tipo: tipoCanon(d.tipo), valor: Number(d.valor ?? 0), porcentaje: d.porcentaje != null ? Number(d.porcentaje) : 0 }))
          : []
      );
      setFirmeza(Array.isArray(data?.Firmeza) ? data.Firmeza : []);
      setPuntajeFinal(data?.PuntajeFinal ?? null);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo cargar el detalle del análisis.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductores();
    fetchVariedades();
    fetchCatalogoCalibres();
    fetchCatalogoDefectos();
    fetchDetalle();
    fetchImagenes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ===== Modal Calibres =====
  const openCalibresModal = async () => {
    if (!catalogoCalibres.length) await fetchCatalogoCalibres();
    const filas = (calibres || []).map((c) => ({ idcalibre: c.idcalibre, valor: Number(c?.valor ?? 0) }));
    setCalibresModal(filas.length ? filas : [{ idcalibre: undefined, valor: 0 }]);
    setCalModalOpen(true);
  };

  const saveCalibres = async () => {
    const invalid = calibresModal.some((f) => f.idcalibre == null);
    if (invalid) { toast.error("Selecciona un calibre en cada fila."); return; }
    try {
      setSavingCal(true);
      const payloadCalibres = calibresModal
        .filter((f) => f.idcalibre != null)
        .map((f) => ({ nuevo_calibre: Number(f.idcalibre), nuevo_valor: Number(f.valor || 0) }));
      const res = await axios.post(
        `${apiUrl}/ServiceFamilyGrowers.asmx/UpdateCalibres`,
        { pid: Number(id), calibres: payloadCalibres },
        { headers: { Authorization: token, "Content-Type": "application/json; charset=utf-8" } }
      );
      const data = parseResponse(res.data) ?? res.data;
      if (data?.success === true) {
        toast.success("Calibres actualizados");
        setCalModalOpen(false);
        await fetchDetalle();
      } else {
        throw new Error(data?.message || "No se pudo guardar los calibres.");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Error al guardar los calibres.");
    } finally {
      setSavingCal(false);
    }
  };

  // ===== Modal Defectos =====
  const openDefectosModal = async () => {
    if (!catalogoDefectos.length) await fetchCatalogoDefectos();
    console.table(catalogoDefectos);
    const byName = new Map(catalogoDefectos.map((x) => [norm(x.nombre), x.iddefectos]));
    const items = (defectos || []).map((d) => {
      const candidateName =
        typeof d.calidad === "string" && d.calidad.trim() !== ""
          ? d.calidad
          : (typeof d.idcalidad === "string" && isNaN(Number(d.idcalidad)) ? d.idcalidad : null);
      let xid = d.idcalidad != null && !isNaN(Number(d.idcalidad)) ? Number(d.idcalidad) : null;
      if (xid == null && candidateName) { const fromCat = byName.get(norm(candidateName)); xid = fromCat ?? null; }
      return { idcalidad: xid, valor: Number(d.valor || 0), tipo: tipoCanon(d.tipo) };
    });
    setDefectosModal(items.length ? items : [{ idcalidad: undefined, valor: 0, tipo: null }]);
    setDefModalOpen(true);
  };

  const saveDefectos = async () => {
    const cleaned = defectosModal
      .map((x) => ({ idNum: Number(x?.idcalidad), valNum: Number(x?.valor ?? 0) }))
      .filter((r) => !Number.isNaN(r.idNum) && !Number.isNaN(r.valNum));
    if (cleaned.length === 0) { toast.warning("No hay filas válidas para guardar."); return; }
    try {
      setSavingDef(true);
      const payloadCalidad = cleaned.map((r) => ({ nuevo_calidad: r.idNum, nuevo_valor: r.valNum }));
      console.log(payloadCalidad);
      const res = await axios.post(
        `${apiUrl}/ServiceFamilyGrowers.asmx/UpdateCalidad`,
        { pid: Number(id), calidad: payloadCalidad },
        { headers: { Authorization: token, "Content-Type": "application/json; charset=utf-8" } }
      );
      const data = parseResponse(res.data) ?? res.data;
      if (data?.success === true) {
        toast.success("Defectos actualizados");
        setDefModalOpen(false);
        await fetchDetalle();
      } else {
        throw new Error(data?.message || "No se pudieron guardar los defectos.");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Error al guardar los defectos.");
    } finally {
      setSavingDef(false);
    }
  };

  // ===== Modal Firmeza =====
  const openFirmezaModal = () => {
    const rows = Array.isArray(firmeza) && firmeza.length
      ? firmeza.map((f) => ({ valor: Number(f?.valor ?? 0) }))
      : [{ valor: 0 }];
    setFirmezaModal(rows);
    setFirModalOpen(true);
  };

  const saveFirmeza = async () => {
    const cleaned = firmezaModal.map((r) => ({ valor: Number(r?.valor) })).filter((r) => !Number.isNaN(r.valor));
    if (!cleaned.length) { toast.warning("Debes ingresar al menos un valor de firmeza."); return; }
    try {
      setSavingFirmeza(true);
      const payload = cleaned.map((r) => ({ nuevo_valor: r.valor }));
      const res = await axios.post(
        `${apiUrl}/ServiceFamilyGrowers.asmx/UpdateFirmeza`,
        { pid: Number(id), firmeza: payload },
        { headers: { Authorization: token, "Content-Type": "application/json; charset=utf-8" } }
      );
      const data = parseResponse(res.data) ?? res.data;
      if (data?.success === true) {
        toast.success("Firmeza actualizada");
        setFirModalOpen(false);
        await fetchDetalle();
      } else {
        throw new Error(data?.message || "No se pudo guardar la firmeza.");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Error al guardar la firmeza.");
    } finally {
      setSavingFirmeza(false);
    }
  };

  // Opciones para selects de catálogos
  const calibreOptions = catalogoCalibres.map((c) => ({ value: c.idcalibre, label: c.nombre }));
  const defectoOptions = catalogoDefectos.map((d) => ({ value: d.iddefectos, label: d.nombre }));
  const productorOptions = productores.map((p) => ({ value: String(p.id), label: p.nombre }));
  const variedadOptions = variedades.map((v) => ({ value: String(v.id), label: v.nombre }));

  const scoreStyle = scoreStyles(puntajeFinal);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-gray-900 m-0">Análisis de Calidad</h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={backToList}>
            <ArrowLeft size={16} /> Volver
          </Button>
          <Button variant="outline" onClick={() => toast.info("Editar…")}>
            <Edit3 size={16} /> Modificar
          </Button>
          <Button onClick={() => toast.success("Enviado al productor")}>
            <Send size={16} /> Enviar al productor
          </Button>
          <Button variant="outline" onClick={() => window.print()}>
            <Printer size={16} /> Imprimir
          </Button>
        </div>
      </div>

      {/* Puntaje Final */}
      <div className="mb-4 flex items-center gap-3">
        <span className="font-bold text-gray-800">Puntaje Final:</span>
        <div
          className="flex items-center justify-center w-14 h-14 rounded-2xl font-black text-2xl shadow-md"
          style={{ background: scoreStyle.bg, color: scoreStyle.fg }}
          title={`Puntaje: ${String(puntajeFinal ?? "—").toUpperCase()}`}
        >
          {String(puntajeFinal ?? "—").toUpperCase()}
        </div>
      </div>

      {/* Encabezado */}
      <Card className="mb-4">
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Fecha</label>
              <Input
                type="date"
                value={enc?.fecha ? enc.fecha.format("YYYY-MM-DD") : ""}
                readOnly
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-500 block mb-1">Productor</label>
              <Select
                value={enc?.idproductor != null ? String(enc.idproductor) : undefined}
                showSearch
                options={productorOptions}
                style={{ width: "100%" }}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Variedad</label>
              <Select
                value={enc?.idvariedad != null ? String(enc.idvariedad) : undefined}
                showSearch
                options={variedadOptions}
                style={{ width: "100%" }}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Número Guía</label>
              <Input value={enc?.numeroguia ?? ""} readOnly />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Temperatura</label>
              <InputNumber value={enc?.temperatura ?? null} readOnly />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Grados Brix</label>
              <InputNumber value={enc?.gradosBrix ?? null} readOnly />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Peso Muestra</label>
              <InputNumber value={enc?.pesomuestra ?? null} readOnly />
            </div>
            <div className="sm:col-span-2 md:col-span-4">
              <label className="text-xs font-medium text-gray-500 block mb-1">Observación</label>
              <Textarea value={enc?.observacion ?? ""} readOnly rows={2} />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Calibres */}
        <Card>
          <CardHeader>
            <CardTitle>Calibres</CardTitle>
            <CardExtra>
              <Button size="sm" variant="outline" onClick={openCalibresModal}>
                <Edit3 size={14} /> Editar
              </Button>
            </CardExtra>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <div className="text-xs font-semibold text-gray-500">Calibre</div>
              <div className="text-xs font-semibold text-gray-500 text-right">Valor</div>
              {calibres.length === 0 && <div className="col-span-2 text-gray-400 text-sm">—</div>}
              {calibres.map((c, i) => (
                <React.Fragment key={i}>
                  <div className="text-sm">{c.calibre ?? c.label ?? c.nombre}</div>
                  <div className="text-sm text-right">{c.valor}</div>
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Defectos */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Defectos de Calidad y Condición</CardTitle>
            <CardExtra>
              <Button size="sm" variant="outline" onClick={openDefectosModal}>
                <Edit3 size={14} /> Editar
              </Button>
            </CardExtra>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {/* Calidad */}
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-2">Calidad</div>
                <div className="grid grid-cols-3 gap-1 text-xs">
                  <div className="font-semibold text-gray-500">Nombre</div>
                  <div className="font-semibold text-gray-500 text-right">Valor</div>
                  <div className="font-semibold text-gray-500 text-right">%</div>
                  {defectosCalidad.length === 0 && <div className="col-span-3 text-gray-400">—</div>}
                  {defectosCalidad.map((d, i) => (
                    <React.Fragment key={i}>
                      <div className="truncate">{nombreDefecto(d)}</div>
                      <div className="text-right">{d.valor}</div>
                      <div className="text-right">{fmtPct(d.porcentaje)}</div>
                    </React.Fragment>
                  ))}
                  <div className="col-span-3 border-t border-gray-100 mt-1 pt-1" />
                  <div className="font-bold">Total</div>
                  <div className="text-right font-bold">{totCalidad.valor}</div>
                  <div className="text-right font-bold">{fmtPct(totCalidad.porcentaje)}</div>
                </div>
              </div>
              {/* Condición */}
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-2">Condición</div>
                <div className="grid grid-cols-3 gap-1 text-xs">
                  <div className="font-semibold text-gray-500">Nombre</div>
                  <div className="font-semibold text-gray-500 text-right">Valor</div>
                  <div className="font-semibold text-gray-500 text-right">%</div>
                  {defectosCondicion.length === 0 && <div className="col-span-3 text-gray-400">—</div>}
                  {defectosCondicion.map((d, i) => (
                    <React.Fragment key={i}>
                      <div className="truncate">{nombreDefecto(d)}</div>
                      <div className="text-right">{d.valor}</div>
                      <div className="text-right">{fmtPct(d.porcentaje)}</div>
                    </React.Fragment>
                  ))}
                  <div className="col-span-3 border-t border-gray-100 mt-1 pt-1" />
                  <div className="font-bold">Total</div>
                  <div className="text-right font-bold">{totCondicion.valor}</div>
                  <div className="text-right font-bold">{fmtPct(totCondicion.porcentaje)}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Firmeza */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Firmeza</CardTitle>
          <CardExtra>
            <span className="text-sm font-semibold text-gray-700">
              Promedio: {promedioFirmeza == null ? "—" : Number(promedioFirmeza).toFixed(2)}
            </span>
            <Button size="sm" variant="outline" onClick={openFirmezaModal}>
              <Edit3 size={14} /> Editar
            </Button>
          </CardExtra>
        </CardHeader>
        <CardContent>
          {firmeza.length === 0 ? (
            <span className="text-gray-400 text-sm">—</span>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
              {firmeza.map((f, i) => (
                <div key={i} className="text-center py-1.5 px-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                  {f.valor}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Imágenes */}
      <Card>
        <CardHeader>
          <CardTitle>Imágenes del análisis</CardTitle>
          <CardExtra>
            <Button size="sm" variant="outline" onClick={fetchImagenes}>Recargar</Button>
            {imagenes?.length > 0 && (
              <Button size="sm" onClick={() => { setImgPreviewIndex(0); setImgPreviewVisible(true); }}>
                Ver todas
              </Button>
            )}
          </CardExtra>
        </CardHeader>
        <CardContent>
          {imagenes?.length === 0 ? (
            <div className="text-gray-400 text-sm">No hay imágenes asociadas.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {imagenes.map((img, idx) => (
                <div
                  key={`${img.nombrearchivo}-${idx}`}
                  className="cursor-pointer group"
                  onClick={() => { setImgPreviewIndex(idx); setImgPreviewVisible(true); }}
                >
                  <img
                    src={img.url}
                    alt={img.nombrearchivo || `img-${idx}`}
                    className="w-full h-28 object-cover rounded-lg border border-gray-200 group-hover:opacity-90 transition-opacity"
                  />
                  <p className="mt-1 text-xs text-gray-500 truncate">{img.nombrearchivo || `Imagen ${idx + 1}`}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Visor de imágenes */}
      <Dialog open={imgPreviewVisible} onOpenChange={setImgPreviewVisible}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {imagenes[imgPreviewIndex]?.nombrearchivo || `Imagen ${imgPreviewIndex + 1}`}
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({imgPreviewIndex + 1} / {imagenes.length})
              </span>
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {imagenes[imgPreviewIndex] && (
              <img
                src={imagenes[imgPreviewIndex].url}
                alt=""
                className="w-full max-h-[70vh] object-contain rounded-lg"
              />
            )}
            <div className="flex justify-between mt-3">
              <Button variant="outline" size="sm" disabled={imgPreviewIndex === 0} onClick={() => setImgPreviewIndex((i) => i - 1)}>
                Anterior
              </Button>
              <Button variant="outline" size="sm" disabled={imgPreviewIndex === imagenes.length - 1} onClick={() => setImgPreviewIndex((i) => i + 1)}>
                Siguiente
              </Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      {/* Modal Calibres */}
      <Dialog open={calModalOpen} onOpenChange={setCalModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 size={16} /> Editar Calibres
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-2 max-h-[60vh] overflow-y-auto">
            {calibresModal.map((row, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_120px_40px] gap-2 items-center">
                <Select
                  options={calibreOptions}
                  value={row.idcalibre}
                  onChange={(v) => setCalibresModal((prev) => prev.map((r, i) => i === idx ? { ...r, idcalibre: v } : r))}
                  placeholder="Calibre"
                  showSearch
                />
                <InputNumber
                  min={0}
                  value={row.valor}
                  onChange={(v) => setCalibresModal((prev) => prev.map((r, i) => i === idx ? { ...r, valor: v ?? 0 } : r))}
                  placeholder="Valor"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={() => setCalibresModal((prev) => prev.filter((_, i) => i !== idx))}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setCalibresModal((prev) => [...prev, { idcalibre: undefined, valor: 0 }])}
            >
              <PlusCircle size={16} /> Agregar fila
            </Button>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCalModalOpen(false)}>Cerrar</Button>
            <Button loading={savingCal} onClick={saveCalibres}>
              <Save size={16} /> Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Defectos */}
      <Dialog open={defModalOpen} onOpenChange={setDefModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 size={16} /> Modificar Calidad
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-2 max-h-[60vh] overflow-y-auto">
            {defectosModal.map((row, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_120px_40px] gap-2 items-center">
                <Select
                  options={defectoOptions}
                  value={row.idcalidad}
                  onChange={(v) => {
                    const tipo = getTipoById(v);
                    setDefectosModal((prev) => prev.map((r, i) => i === idx ? { ...r, idcalidad: v, tipo } : r));
                  }}
                  placeholder="Nombre"
                  showSearch
                />
                <InputNumber
                  min={0}
                  value={row.valor}
                  onChange={(v) => setDefectosModal((prev) => prev.map((r, i) => i === idx ? { ...r, valor: v ?? 0 } : r))}
                  placeholder="Cantidad"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={() => setDefectosModal((prev) => prev.filter((_, i) => i !== idx))}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setDefectosModal((prev) => [...prev, { idcalidad: undefined, valor: 0, tipo: null }])}
            >
              <PlusCircle size={16} /> Agregar Fila
            </Button>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDefModalOpen(false)}>Cerrar</Button>
            <Button loading={savingDef} onClick={saveDefectos}>
              <Save size={16} /> Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Firmeza */}
      <Dialog open={firModalOpen} onOpenChange={setFirModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 size={16} /> Editar Firmeza
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-2 max-h-[60vh] overflow-y-auto">
            {firmezaModal.map((row, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_40px] gap-2 items-center">
                <InputNumber
                  min={0}
                  step={0.01}
                  value={row.valor}
                  onChange={(v) => setFirmezaModal((prev) => prev.map((r, i) => i === idx ? { ...r, valor: v ?? 0 } : r))}
                  placeholder="Valor de firmeza"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={() => setFirmezaModal((prev) => prev.filter((_, i) => i !== idx))}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setFirmezaModal((prev) => [...prev, { valor: 0 }])}
            >
              <PlusCircle size={16} /> Agregar fila
            </Button>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFirModalOpen(false)}>Cerrar</Button>
            <Button loading={savingFirmeza} onClick={saveFirmeza}>
              <Save size={16} /> Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
